/**
 * Service avancé pour le filtrage des châteaux avec filtres par âge, thème, taille
 */

export interface AdvancedFilterCriteria {
  space?: string;
  budget?: string;
  ageGroup?: string;
  theme?: string;
  size?: string;
  features?: string[];
}

export interface ChateauWithFilters {
  id: string;
  nom: string;
  taille: string;
  capacite: string;
  prix: string;
  description: string;
  caracteristiques: string[];
  age_recommande: string;
  theme?: string;
  surface?: number;
  features?: string[];
}

export class AdvancedChateauFilterService {
  private static readonly BUDGET_RANGES: Record<string, { min: number; max: number }> = {
    "30-60€": { min: 30, max: 60 },
    "60-90€": { min: 60, max: 90 },
    "90-120€": { min: 90, max: 120 },
    "120€+": { min: 120, max: 999 },
    "tous": { min: 0, max: 999 }
  };

  private static readonly AGE_GROUPS = {
    "3-6 ans": { min: 3, max: 6 },
    "6-10 ans": { min: 6, max: 10 },
    "10-14 ans": { min: 10, max: 14 },
    "tous": { min: 0, max: 99 }
  };

  private static readonly THEMES = [
    "disco", "princesse", "animaux", "aventure", "festif", "nature"
  ];

  private static readonly SIZES: Record<string, { min?: number; max?: number }> = {
    "petit": { max: 15 }, // jusqu'à 15m²
    "moyen": { min: 15, max: 25 }, // 15-25m²
    "grand": { min: 25 }, // plus de 25m²
  };

  /**
   * Extrait les dimensions et calcule la surface
   */
  private static calculateSurface(taille: string): number {
    const cleanTaille = taille.replace(/,/g, '.');
    const dimensions = cleanTaille.match(/(\d+(?:\.\d+)?)/g);
    
    if (dimensions && dimensions.length >= 2) {
      const length = parseFloat(dimensions[0]);
      const width = parseFloat(dimensions[1]);
      return length * width;
    }
    
    return 0;
  }

  /**
   * Détermine le thème d'un château basé sur son nom et ses caractéristiques
   */
  private static determineTheme(chateau: ChateauWithFilters): string {
    const name = chateau.nom.toLowerCase();
    const features = chateau.caracteristiques.join(' ').toLowerCase();
    
    if (name.includes('disco') || features.includes('musique') || features.includes('lumière')) {
      return 'disco';
    }
    if (name.includes('princesse')) {
      return 'princesse';
    }
    if (name.includes('dino') || name.includes('grenouille') || name.includes('vache') || name.includes('pingouin')) {
      return 'animaux';
    }
    if (features.includes('toboggan') || features.includes('aventure')) {
      return 'aventure';
    }
    if (features.includes('festif') || features.includes('fête')) {
      return 'festif';
    }
    
    return 'nature';
  }

  /**
   * Vérifie si le château correspond aux critères d'âge
   */
  private static matchesAgeCriteria(chateau: ChateauWithFilters, ageCriteria: string): boolean {
    if (!ageCriteria || ageCriteria === "tous") return true;
    
    const ageRange = this.AGE_GROUPS[ageCriteria as keyof typeof this.AGE_GROUPS];
    if (!ageRange) return true;

    // Parse l'âge recommandé du château (ex: "3-12 ans")
    const ageMatch = chateau.age_recommande.match(/(\d+)-(\d+)/);
    if (ageMatch) {
      const chateauMinAge = parseInt(ageMatch[1]);
      const chateauMaxAge = parseInt(ageMatch[2]);
      
      // Vérifie s'il y a chevauchement entre les tranches d'âge
      return !(ageRange.max < chateauMinAge || ageRange.min > chateauMaxAge);
    }
    
    return true;
  }

  /**
   * Vérifie si le château correspond aux critères de thème
   */
  private static matchesThemeCriteria(chateau: ChateauWithFilters, themeCriteria: string): boolean {
    if (!themeCriteria || themeCriteria === "tous") return true;
    
    const chateauTheme = this.determineTheme(chateau);
    return chateauTheme === themeCriteria;
  }

  /**
   * Vérifie si le château correspond aux critères de taille
   */
  private static matchesSizeCriteria(chateau: ChateauWithFilters, sizeCriteria: string): boolean {
    if (!sizeCriteria || sizeCriteria === "tous") return true;
    
    const surface = this.calculateSurface(chateau.taille);
    const sizeRange = this.SIZES[sizeCriteria as keyof typeof this.SIZES];
    
    if (!sizeRange || surface === 0) return true;
    
    if (sizeRange.min !== undefined && sizeRange.max !== undefined) {
      return surface >= sizeRange.min && surface <= sizeRange.max;
    } else if (sizeRange.min !== undefined) {
      return surface >= sizeRange.min;
    } else if (sizeRange.max !== undefined) {
      return surface <= sizeRange.max;
    }
    
    return true;
  }

  /**
   * Vérifie si le château correspond aux critères de caractéristiques
   */
  private static matchesFeaturesCriteria(chateau: ChateauWithFilters, featuresCriteria: string[]): boolean {
    if (!featuresCriteria || featuresCriteria.length === 0) return true;
    
    const chateauFeatures = chateau.caracteristiques.map(f => f.toLowerCase());
    
    return featuresCriteria.some(feature => 
      chateauFeatures.some(cf => cf.includes(feature.toLowerCase()))
    );
  }

  /**
   * Filtre les châteaux selon les critères avancés
   */
  static filterChateaux(chateaux: ChateauWithFilters[], criteria: AdvancedFilterCriteria): ChateauWithFilters[] {
    if (!chateaux.length) return [];

    return chateaux.filter(chateau => {
      // Filtres de base (compatibilité avec l'ancien système)
      if (criteria.space && !this.matchesSpaceCriteria(chateau, criteria.space)) {
        return false;
      }
      
      if (criteria.budget && !this.matchesBudgetCriteria(chateau, criteria.budget)) {
        return false;
      }

      // Nouveaux filtres avancés
      if (!this.matchesAgeCriteria(chateau, criteria.ageGroup || "")) {
        return false;
      }

      if (!this.matchesThemeCriteria(chateau, criteria.theme || "")) {
        return false;
      }

      if (!this.matchesSizeCriteria(chateau, criteria.size || "")) {
        return false;
      }

      if (!this.matchesFeaturesCriteria(chateau, criteria.features || [])) {
        return false;
      }

      return true;
    });
  }

  /**
   * Méthodes héritées du service de base
   */
  private static matchesSpaceCriteria(chateau: ChateauWithFilters, spaceCriteria: string): boolean {
    if (spaceCriteria === "Petit (largeur max de 4m)") {
      const surface = this.calculateSurface(chateau.taille);
      return surface <= 16; // 4m x 4m
    }
    return true;
  }

  private static matchesBudgetCriteria(chateau: ChateauWithFilters, budgetCriteria: string): boolean {
    const budgetRange = this.BUDGET_RANGES[budgetCriteria];
    if (!budgetRange) return false;

    const prix = parseInt(chateau.prix.replace(/[^\d]/g, ''));
    return prix >= budgetRange.min && prix <= budgetRange.max;
  }

  /**
   * Options pour les filtres
   */
  static getSpaceOptions() {
    return [
      { value: "", label: "Tous les espaces" },
      { value: "Petit (largeur max de 4m)", label: "Petit espace (max 4m)" },
      { value: "Moyen (max 6m x 6m)", label: "Espace moyen (6x6m)" },
      { value: "Grand (plus de 6m x 6m)", label: "Grand espace (6m+)" }
    ];
  }

  static getBudgetOptions() {
    return [
      { value: "", label: "Tous les budgets" },
      { value: "30-60€", label: "30-60€" },
      { value: "60-90€", label: "60-90€" },
      { value: "90-120€", label: "90-120€" },
      { value: "120€+", label: "120€ et plus" }
    ];
  }

  static getAgeOptions() {
    return [
      { value: "", label: "Tous les âges" },
      { value: "3-6 ans", label: "3-6 ans (petits)" },
      { value: "6-10 ans", label: "6-10 ans (moyens)" },
      { value: "10-14 ans", label: "10-14 ans (grands)" }
    ];
  }

  static getThemeOptions() {
    return [
      { value: "", label: "Tous les thèmes" },
      { value: "disco", label: "🎵 Disco & Musique" },
      { value: "princesse", label: "👑 Princesse" },
      { value: "animaux", label: "🐾 Animaux" },
      { value: "aventure", label: "🏰 Aventure" },
      { value: "festif", label: "🎉 Festif" },
      { value: "nature", label: "🌿 Nature" }
    ];
  }

  static getSizeOptions() {
    return [
      { value: "", label: "Toutes les tailles" },
      { value: "petit", label: "Petit (jusqu'à 15m²)" },
      { value: "moyen", label: "Moyen (15-25m²)" },
      { value: "grand", label: "Grand (25m²+)" }
    ];
  }

  static getFeatureOptions() {
    return [
      { value: "toboggan", label: "🛝 Toboggan" },
      { value: "musique", label: "🎵 Musique" },
      { value: "lumière", label: "💡 Éclairage" },
      { value: "thème", label: "🎨 Thème spécial" },
      { value: "grand", label: "📏 Grande taille" }
    ];
  }
}